'use client';

import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'next/navigation';
import { InstantSearchServerState, useClearRefinements } from 'react-instantsearch-hooks';
import ProductList from 'components/commercetools-ui/organisms/product/product-list-algolia';
import ProductListProvider, {
  useProductList,
} from 'components/commercetools-ui/organisms/product/product-list-algolia/context';
import {
  FacetConfiguration,
  PriceConfiguration,
} from 'components/commercetools-ui/organisms/product/product-list-algolia/types';
import InstantSearch from 'components/HOC/InstantSearch';
import usePath from 'helpers/hooks/usePath';
import { flattenTree } from 'helpers/utils/flattenTree';
import { getLocalizationInfo } from 'project.config';
import LocalizedIndex from 'providers/algolia/localized-index';
import { TasticProps } from 'frontastic/tastics/types';
import CollectionProductListAlgolia from 'components/commercetools-ui/organisms/product/collection-product-list-algolia';
import ContentListAlgolia from './content-list';
import { useHits, Index, Configure } from 'react-instantsearch-hooks-web';
import CustomStats from 'components/commercetools-ui/atoms/search-algolia/customstats';
import { SuggestionsFetcher } from 'components/commercetools-ui/organisms/no-result';
import { productsQuerySuggestionsIndex_AU, productsQuerySuggestionsIndex_NZ } from 'helpers/constants/algolia';

export interface Props {
  slug?: string;
  searchQuery?: string;
  facetsConfiguration: FacetConfiguration[];
  pricesConfiguration: PriceConfiguration[];
  serverUrl: string;
  serverState?: InstantSearchServerState;
  setProductResults?: any;
  suggestions?:any;
}

function ProductListTastic({
  noContentSuggestions,
  categories,
  setProductResults,
  data,
  suggestions
}: TasticProps<Props>) {
  const { updateFacetsConfiguration, updatePricesConfiguration } = useProductList();
  const { locale, slug } = useParams();
  const { hits, results } = useHits();

  const { currency } = getLocalizationInfo(locale);

  const categorySlug = (slug as string).split('/').at(-1);

  const searchParams = useSearchParams();

  const searchQuery = searchParams.get('query') as string;

  const { refine: clearAllRefinements } = useClearRefinements();
  const { pathWithoutQuery } = usePath();
  const isCollectionPage = pathWithoutQuery.includes('collection') || pathWithoutQuery.includes('livewell') || pathWithoutQuery.includes('cleanflush') || pathWithoutQuery.includes('caroma-easy-switch') || pathWithoutQuery.includes('smart-command') || pathWithoutQuery.includes('homecare') || pathWithoutQuery.includes('cosmo') || pathWithoutQuery.includes('elegance') || pathWithoutQuery.includes('cirrus') || pathWithoutQuery.includes('sahara') || pathWithoutQuery.includes('acacia');
  const facetsConfiguration = useMemo<Record<string, FacetConfiguration>>(() => {
    return (data.facetsConfiguration ?? []).reduce(
      (acc, configuration) => ({
        ...acc,
        [configuration.key.replace(/\{currency\}/, currency)]: {
          type: configuration.type,
          label: configuration.label,
        } as FacetConfiguration,
      }),
      {},
    );
  }, [data.facetsConfiguration, currency]);

  const pricesConfiguration = useMemo<Record<string, PriceConfiguration>>(() => {
    return (data.pricesConfiguration ?? []).reduce(
      (acc, configuration) => ({
        ...acc,
        [configuration.key]: {
          ranges: configuration.ranges,
        } as PriceConfiguration,
      }),
      {},
    );
  }, [data.pricesConfiguration]);

  useEffect(() => {
    updateFacetsConfiguration(facetsConfiguration);
    updatePricesConfiguration(pricesConfiguration);
  }, [facetsConfiguration, pricesConfiguration, updateFacetsConfiguration, updatePricesConfiguration]);

  useEffect(() => {
    clearAllRefinements();
  }, [clearAllRefinements, pathWithoutQuery]);

  const flattenedCategories = categories.map((category) => flattenTree(category, 'subCategories')).flat();

  const isCategoryFoundOrSearchQueryExists = useMemo(() => {
    if (searchQuery) return true;
    if (!categorySlug) return true;

    return !!flattenedCategories?.find((category) => category.slug === categorySlug);
  }, [searchQuery, flattenedCategories, categorySlug]);

  // if (!isCategoryFoundOrSearchQueryExists) return <NotFound />;

  useEffect(() => {
    setProductResults(results);
  }, [results]);

  
  return (
    <>
      {isCollectionPage ? (
        <CollectionProductListAlgolia
          slug={categorySlug}
          searchQuery={searchQuery}
          categories={flattenedCategories}
          facetsConfiguration={facetsConfiguration}
        />
      ) : (
        <ProductList
          slug={categorySlug}
          searchQuery={searchQuery}
          categories={flattenedCategories}
          facetsConfiguration={facetsConfiguration}
          noContentSuggestions={noContentSuggestions}
          suggestions={suggestions}
        />
      )}
    </>
  );
}

function ProductListAlgoliaTastic({ data, ...props }: TasticProps<Props>) {
  const [noContentSuggestions, setNoContentSuggestion] = useState<number>(0);
  const { locale, slug } = useParams();
  const searchParams = useSearchParams();

  const searchQuery = searchParams.get('query') as string;
  const [productQuery, setProductQuery] = useState('');
  const [productsResults, setProductResults] = useState<any>();
  const [suggestions, setSuggestions] = useState<any>([]);
  const isSearchPageWithNoProducts = slug === 'search' && !!searchQuery && productsResults?.nbHits === 0;
  const indexes = {
    products: {
      au: productsQuerySuggestionsIndex_AU,
      nz: productsQuerySuggestionsIndex_NZ,
    },
  };

  return (
    <InstantSearch>
      {isSearchPageWithNoProducts &&  <Index indexName={indexes['products'][locale as 'au' | 'nz']}>
         <Configure hitsPerPage={5} />
         <SuggestionsFetcher setSuggestions={setSuggestions} query={searchQuery}/>
      </Index>  }      
      <LocalizedIndex type="products">
        <ProductListProvider>
          <CustomStats setProductQuery={setProductQuery} searchQuery={searchQuery} />
          <ProductListTastic
            noContentSuggestions={noContentSuggestions}
            setProductResults={setProductResults}
            data={{ ...data }}
            suggestions={suggestions}
            {...props}
          />
        </ProductListProvider>
      </LocalizedIndex>
      <LocalizedIndex type="content">
        <ContentListAlgolia
          searchQuery={searchQuery}
          productQuery={productQuery}
          setNoContentSuggestion={setNoContentSuggestion}
          productResults={productsResults}
        />
      </LocalizedIndex>
    </InstantSearch>
  );
}

export default ProductListAlgoliaTastic;
